import { isEqual } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';

import { UnsavedChangesDialog } from './UnsavedChangesDialog';

type RegistrationContextValue = {
  registerDirty: (id: string, isDirty: boolean) => void;
  unregisterDirty: (id: string) => void;
};

type ActionContextValue = {
  confirmIfDirty: (action: () => void) => void;
};

const RegistrationContext = createContext<RegistrationContextValue | null>(
  null,
);
const ActionContext = createContext<ActionContextValue | null>(null);

type Props = {
  title?: string;
  message?: string;
  children: React.ReactNode;
};

export const UnsavedChangesGuardProvider: React.FC<Props> = ({
  title,
  message,
  children,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.unsaved_changes',
  });

  const [dirtySources, setDirtySources] = useState<Record<string, boolean>>({});
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const isDirty = useMemo(
    () => Object.values(dirtySources).some(Boolean),
    [dirtySources],
  );

  const registerDirty = useCallback((id: string, dirty: boolean) => {
    setDirtySources((prev) => {
      if (prev[id] === dirty) {
        return prev;
      }
      return { ...prev, [id]: dirty };
    });
  }, []);

  const unregisterDirty = useCallback((id: string) => {
    setDirtySources((prev) => {
      if (!(id in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const confirmIfDirty = useCallback(
    (action: () => void) => {
      if (isDirty) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [isDirty],
  );

  const onStay = useCallback(() => setPendingAction(null), []);
  const onLeave = useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  }, [pendingAction]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const registrationValue = useMemo(
    () => ({ registerDirty, unregisterDirty }),
    [registerDirty, unregisterDirty],
  );

  const actionValue = useMemo(() => ({ confirmIfDirty }), [confirmIfDirty]);

  return (
    <RegistrationContext.Provider value={registrationValue}>
      <ActionContext.Provider value={actionValue}>
        {children}
        <UnsavedChangesDialog
          open={!!pendingAction}
          title={title || t('title')}
          message={message || t('message')}
          stayLabel={t('stay_btn')}
          leaveLabel={t('leave_btn')}
          onStay={onStay}
          onLeave={onLeave}
        />
      </ActionContext.Provider>
    </RegistrationContext.Provider>
  );
};

type DirtyComparable = {
  values: unknown;
  initialValues: unknown;
};

export const useUnsavedChangesGuard = ({
  values,
  initialValues,
}: DirtyComparable) => {
  const ctx = useContext(RegistrationContext);
  const id = useId();

  const isDirty = useMemo(
    () => !isEqual(values, initialValues),
    [values, initialValues],
  );

  useEffect(() => {
    if (!ctx) {
      return;
    }
    ctx.registerDirty(id, isDirty);
  }, [ctx, id, isDirty]);

  useEffect(() => {
    if (!ctx) {
      return;
    }
    return () => ctx.unregisterDirty(id);
  }, [ctx, id]);
};

export const useUnsavedChangesGuardSafe = () => {
  const ctx = useContext(ActionContext);
  return useMemo(
    () => ({
      confirmIfDirty: ctx?.confirmIfDirty ?? ((action: () => void) => action()),
    }),
    [ctx],
  );
};
